/**
 * Setup, as three steps in the order they happen.
 *
 * ## Audited 2026-09-03
 *
 * Arun: *"can you audit everything thats in setup and make sure its very intuitive and guides
 * the user step by step please. thnk about refactoring deisgn about face deisnf principles
 * etc."*
 *
 * The content here was already careful — every control said what it would do before it did it.
 * What it lacked was **orientation**: three cards of equal visual weight, no stated order, and
 * no way to tell whether you were done. A reader arriving at "8loop connection" and "Where the
 * code action posts" has to work out for themselves that these are two halves of one idea and
 * which one to do first. About Face's word for the fix is a *sequence with an evident goal*;
 * Refactoring UI's is *hierarchy* — and the two agree here, because the hierarchy has to encode
 * the sequence.
 *
 * So:
 *
 *  1. **The mental model comes first, in one sentence.** Two credentials, pointing in opposite
 *     directions. Every question anybody has asked about this page — *"what is the ingest
 *     token? is that same as api key?"* — dissolves once that is said out loud, and it cannot
 *     be inferred from two cards that each describe one end.
 *  2. **The cards are numbered and named as the reader's question**, not as the resource they
 *     configure: "Can this app reach the tenant?" and "Can the tenant reach this app?"
 *  3. **Each step carries its own verdict** at the top — done, not done, never checked — so
 *     "where am I?" is answered by looking rather than by reading three paragraphs.
 *  4. **The page ends by naming what comes next**, which is not on this tab: adding an agent
 *     and installing its hook. Setup finishing with silence is what makes a person hunt.
 *
 * The dry-run tester is step 3 and lives in `dry-run-section.tsx` — it grew into a form with a
 * channel sweep in it and no longer fits under a heading in this file.
 */

import { useState } from "react";
import { ArrowRight, CheckCircle2, Circle, KeyRound, MinusCircle, RefreshCw, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { SettingsSection } from "@/components/ui/settings-section";
import { CopyableValue } from "@/components/ui/copyable-value";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DetailField, DetailFields } from "@/components/ui/detail-fields";
import { cn } from "@/lib/utils";
import { errorMessage, type AccountConfig } from "@/lib/api";
import { relativeTime } from "@/lib/labels";
import { useCheckConnection, useRotateIngestToken } from "@/lib/queries";
import { CredentialEditor } from "./credential-editor";
import { DryRunSection } from "./dry-run-section";

export function SetupSection({
  accountId,
  config,
}: {
  accountId: string;
  config: AccountConfig;
}) {
  const rotate = useRotateIngestToken(accountId);
  const check = useCheckConnection(accountId);

  const [newToken, setNewToken] = useState<string | null>(null);
  const [editingCredential, setEditingCredential] = useState(false);

  const creds = config.credentials;

  /** Where each step stands, so the reader is not the one keeping score. */
  const connected = creds.mode !== "none" && creds.last_check_ok === true;
  const step1: StepState =
    creds.mode === "none"
      ? { tone: "todo", label: "Not connected" }
      : creds.last_check_ok === false
        ? { tone: "broken", label: "8loop rejected the key" }
        : creds.last_check_at === null
          ? { tone: "unknown", label: "Never tested" }
          : { tone: "done", label: `Working, checked ${relativeTime(creds.last_check_at)}` };
  const step2: StepState = !creds.has_ingest_token
    ? { tone: "todo", label: "No token yet" }
    : creds.ingest_token_readable
      ? { tone: "done", label: "Working — the app puts the token in every hook it generates" }
      : // Reached only when recovery has already been tried and failed on every agent, because
        // the agent page's plan read attempts it silently on every load.
        { tone: "unknown", label: "Token exists, and no installed hook carries it" };

  /**
   * Replacing the token, which is almost never the right answer.
   *
   * Defined once and rendered in two places: bare, for an account whose token the app cannot
   * read (there it is the only way forward), and inside a disclosure otherwise (there it is a
   * leak response and nothing else). Arun: *"not clear when to press rotate token and why"* —
   * so the control now appears only where an answer to "why" exists.
   */
  const rotateControl = (
            /*
              Rotating is irreversible and it breaks a *live* integration the instant it
              lands: the tenant's code action keeps posting the old token and every
              conversation is 401'd until somebody pastes the new one in. That is an
              outage caused by one click on an outline button that looked like "Test
              connection" — hence the destructive colour and a confirmation that says
              what stops working.
            */
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={rotate.isPending}>
                  <RefreshCw className="size-3.5" />
                  Rotate token
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Rotate the ingest token for {config.account.name}?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    The existing token stops working immediately. Every finished call the
                    tenant's hooks post is rejected with a 401 until each agent's hook is
                    reinstalled from a fresh brief — so no callbacks arrive in the meantime, and
                    they are not queued or replayed. Nobody has to keep the new token: it is
                    stored encrypted, and the app puts it into every brief from then on.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep the current token</AlertDialogCancel>
                  <AlertDialogAction
                    variant="destructive"
                    onClick={() =>
                      rotate.mutate(undefined, {
                        onSuccess: (r) => {
                          setNewToken(r.ingest_token);
                          toast.success(r.message);
                        },
                        onError: (e) => toast.error(errorMessage(e)),
                      })
                    }
                  >
                    Rotate it
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
  );

  return (
    <>
      {/*
        The one sentence the whole tab depends on, before any control. Both credentials are
        called "the key" by somebody at some point, and the questions this page has earned —
        "what is the ingest token? is that same as api key? where do i get it from?" — are all
        the same question: which direction does this one point in.
      */}
      <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-4">
        <p className="text-sm font-medium">Two connections, pointing in opposite directions.</p>
        <p className="text-muted-foreground text-sm">
          <span className="text-foreground">This app calls 8loop</span> to place a callback — that
          needs the tenant's <span className="text-foreground">API key</span>, which 8loop issues
          and you paste in below.{" "}
          <span className="text-foreground">8loop calls this app</span> when one of its calls
          ends — that needs an <span className="text-foreground">ingest token</span>, which{" "}
          <em>this</em> app issues, and a URL to post to. Get both right and finished calls start
          arriving; each agent still needs its own hook installed, which is the Agents tab.
        </p>
      </div>

      <SettingsSection
        title="1. Can this app reach the tenant?"
        description="The 8loop API key this account dials with. Issued in 8loop, pasted here, stored encrypted."
        footer={
          <div className="flex flex-1 flex-wrap items-center justify-between gap-3">
            {/*
              Arun: "i am scarted to press test connection not knowing what it will
              do." Fair — a button called "Test" next to a telephony integration could
              plausibly ring someone. It cannot: the endpoint does one thing, a GET for
              the tenant's agent list. Saying so next to the button is the whole fix,
              and it belongs *before* the click, not in the result toast.
            */}
            <p className="text-[11px] text-muted-foreground">
              Asks 8loop for this tenant's agent list. It places no call, writes nothing, and
              touches no customer data — it just proves the credential works.
            </p>
            <div className="flex shrink-0 gap-2">
              {!editingCredential && creds.mode !== "none" && (
                <Button variant="outline" size="sm" onClick={() => setEditingCredential(true)}>
                  <KeyRound className="size-3.5" />
                  Replace API key
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={check.isPending || creds.mode === "none"}
                onClick={() =>
                  check.mutate(undefined, {
                    onSuccess: (r) => toast.success(r.message),
                    onError: (e) => toast.error(errorMessage(e)),
                  })
                }
              >
                {check.isPending ? (
                  <RefreshCw className="size-3.5 animate-spin" />
                ) : (
                  <CheckCircle2 className="size-3.5" />
                )}
                Test connection
              </Button>
            </div>
          </div>
        }
      >
        <StepVerdict state={step1} />
        <DetailFields>
          <DetailField label="Tenant">{config.account.tenant_host}</DetailField>
          <DetailField label="API base URL" mono>
            {config.account.base_url}
          </DetailField>
          <DetailField label="API key">
            {creds.mode === "api_key" ? (
              <>
                <Badge variant="secondary" className="gap-1">
                  <KeyRound className="size-3" />
                  Stored
                </Badge>
                <span className="font-mono text-xs text-muted-foreground">
                  {creds.api_key_masked}
                </span>
              </>
            ) : (
              <span className="text-destructive">none stored — nothing can be read or dialled</span>
            )}
          </DetailField>
          {/*
            The stored verdict, so the answer survives a page reload. Without it this
            card could only say what mode is configured — and "Email and password" over
            a password 8loop has been 401-ing looks identical to one that works.
          */}
          <DetailField label="Last verified">
            {creds.mode === "none" ? (
              <span className="text-muted-foreground">nothing to verify yet</span>
            ) : creds.last_check_at === null ? (
              <span className="text-warning">
                never — press Test connection to find out before a callback needs dialling
              </span>
            ) : creds.last_check_ok ? (
              <span className="text-success">
                {relativeTime(creds.last_check_at)} — 8loop accepted it
              </span>
            ) : (
              <span className="text-destructive">
                {relativeTime(creds.last_check_at)} — 8loop rejected it:{" "}
                {creds.last_check_error ?? "no reason given"}
              </span>
            )}
          </DetailField>
          <DetailField label="Double-dial protection">
            {creds.supports_idempotency ? (
              <span className="text-success">
                Idempotency-Key honoured — a crash mid-dial cannot place two calls
              </span>
            ) : (
              <span className="text-warning">
                No API key, so 8loop would ignore Idempotency-Key. A crash between sending a dial
                and recording it could ring the same person twice — the dialler refuses to resume
                rather than risk it, which stalls the callback instead.
              </span>
            )}
          </DetailField>
        </DetailFields>

        {/*
          Open unprompted when there is no key. The form used to appear only after
          pressing "Replace credential", which for an account with nothing stored meant
          the one thing you had come to do was behind a button that described a different
          thing — "there isnt a way for me to add the api token for the individual
          accounts Eg., Supernova".
        */}
        {creds.mode === "none" ? (
          <CredentialEditor accountId={accountId} credentials={creds} />
        ) : (
          editingCredential && (
            <CredentialEditor
              accountId={accountId}
              credentials={creds}
              onDone={() => setEditingCredential(false)}
            />
          )
        )}

        {creds.credential_error && (
          <Alert variant="destructive">
            <XCircle className="size-4" />
            <AlertDescription>
              The stored credential cannot be read: {creds.credential_error}. Enter it again.
            </AlertDescription>
          </Alert>
        )}

        {check.isSuccess && (
          <Alert>
            <CheckCircle2 className="size-4" />
            <AlertDescription>{check.data.message}</AlertDescription>
          </Alert>
        )}
        {check.isError && (
          <Alert variant="destructive">
            <XCircle className="size-4" />
            <AlertDescription>{errorMessage(check.error)}</AlertDescription>
          </Alert>
        )}
      </SettingsSection>

      <SettingsSection
        title="2. Can the tenant reach this app?"
        description="The URL 8loop posts a finished call to, and the token that says which account it belongs to. Both are baked into the hook installed on each agent, so you rarely need to copy them by hand."
        footer={
          // Rotation is not an action anybody should reach for casually: it 401s every hook on
          // the account until each is reinstalled. Arun: *"not clear when to press rotate token
          // and why"* — the honest answer is "almost never", so it is behind a disclosure that
          // says the one case.
          creds.has_ingest_token && creds.ingest_token_readable ? (
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer hover:text-foreground hover:underline">
                If the token has leaked, replace it
              </summary>
              <div className="mt-2 space-y-2">
                <p>
                  Issuing a new one kills the current token immediately. Every hook installed on
                  this account — on every agent — starts getting 401s until each is reinstalled
                  from a fresh brief. There is no other reason to do this: the app already has the
                  token and never needs you to supply it.
                </p>
                {rotateControl}
              </div>
            </details>
          ) : creds.has_ingest_token ? (
            rotateControl
          ) : (
            // Nothing to break: there is no token, so issuing one only ever helps.
            <Button
              variant="outline"
              size="sm"
              disabled={rotate.isPending}
              onClick={() =>
                rotate.mutate(undefined, {
                  onSuccess: (r) => {
                    setNewToken(r.ingest_token);
                    toast.success(r.message);
                  },
                  onError: (e) => toast.error(errorMessage(e)),
                })
              }
            >
              <RefreshCw className="size-3.5" />
              Issue token
            </Button>
          )
        }
      >
        <StepVerdict state={step2} />
        <CopyableValue label="Ingest URL" value={config.ingest_url} mono />
        {/*
          Not a CopyableValue. It was one, and its payload was the literal string
          "Bearer <ingest token>" — a copy button that hands you a placeholder to fill in by
          hand, on a card whose own description says you rarely need to copy any of this. The
          header is worth stating so a 401 is diagnosable; it is not worth offering to copy.
        */}
        <p className="text-muted-foreground text-xs">
          Posted with the header{" "}
          <code className="font-mono">Authorization: Bearer &lt;this account's ingest token&gt;</code>
          . Each brief fills the token in for you.
        </p>
        {/*
          Said in place because the host above is derived from wherever this app is
          being served, not written down anywhere — so on localhost it reads
          localhost, and a code action pasted from a dev session will silently post
          to nothing once it is live on a tenant. Naming the variable makes a
          wrong-looking host a two-minute fix instead of a mystery.
        */}
        <p className="text-xs text-muted-foreground">
          The host comes from this deployment, not from a setting on this page —{" "}
          <code className="font-mono">PUBLIC_URL</code>, falling back to Render's own service URL.
          In production that should read{" "}
          <code className="font-mono">https://callback.eightloop.ai</code>; if it says{" "}
          <code className="font-mono">onrender.com</code> or{" "}
          <code className="font-mono">localhost</code>, this URL is only good for whoever can reach
          that host.
        </p>

        {newToken ? (
          <Alert>
            <KeyRound className="size-4" />
            <AlertDescription className="space-y-2">
              <p className="font-medium">
                The new token. Every hook already installed on this account is now getting a 401
                — open each agent's page and hand over a fresh brief.
              </p>
              <code className="bg-muted block rounded p-2 font-mono text-xs break-all">
                {newToken}
              </code>
              <p className="text-xs">
                You do not need to keep a copy: it is stored encrypted, and this app puts it into
                every brief and script it generates from now on.
              </p>
            </AlertDescription>
          </Alert>
        ) : creds.ingest_token_readable ? (
          <p className="text-muted-foreground text-sm">
            Nothing to do here. The token is issued and stored encrypted, so the app puts it into
            every hook and brief it generates — you never have to copy it, and there is no reason
            to touch it again.
          </p>
        ) : creds.has_ingest_token ? (
          <Alert>
            <KeyRound className="size-4" />
            <AlertDescription>
              This account's token predates the app keeping a readable copy, and no hook installed
              from this app carries it either — so the app cannot put it into a brief. Nothing is
              broken: every hook already installed keeps working. The fix is to issue a new one
              below, which means handing over a fresh brief for each agent, because the old token
              dies the moment the new one exists.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="destructive">
            <XCircle className="size-4" />
            <AlertDescription>
              No ingest token yet, so anything posted for this account is rejected with a 401 and
              no callback can ever arrive. Issuing one breaks nothing — there is nothing using the
              old one, because there is no old one.
            </AlertDescription>
          </Alert>
        )}
      </SettingsSection>

      <DryRunSection accountId={accountId} config={config} />

      {/*
        A page that ends in silence is a page somebody hunts from. The next thing to do is not
        on this tab at all, and there is no way to know that by looking.
      */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 p-4">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium">
            {connected && creds.has_ingest_token
              ? "This account is connected both ways. Next: which agents do callbacks?"
              : "Once both directions work, the next step is on the Agents tab."}
          </p>
          <p className="text-muted-foreground text-sm">
            Nothing arrives until a post-call hook is installed on an agent — that is per agent,
            on its own page, and it is what turns a finished call into a row in the queue.
          </p>
        </div>
        <Button asChild size="sm" className="shrink-0">
          <Link to={`/accounts/${accountId}/settings?tab=agents`}>
            Go to Agents
            <ArrowRight className="size-3.5" />
          </Link>
        </Button>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ a step's verdict ---

type StepState = {
  tone: "done" | "todo" | "broken" | "unknown";
  label: string;
};

/**
 * One line at the top of a step saying where it stands.
 *
 * Four tones, not two, because "never tested" is neither done nor broken — and colouring it
 * red sends somebody to fix a credential that has never had a chance to fail. That distinction
 * is the same one the agents list makes about a hook, for the same reason.
 */
function StepVerdict({ state }: { state: StepState }): React.ReactElement {
  const icon =
    state.tone === "done" ? (
      <CheckCircle2 className="text-success mt-0.5 size-4 shrink-0" />
    ) : state.tone === "broken" ? (
      <XCircle className="text-destructive mt-0.5 size-4 shrink-0" />
    ) : state.tone === "todo" ? (
      <Circle className="text-warning mt-0.5 size-4 shrink-0" />
    ) : (
      <MinusCircle className="text-muted-foreground mt-0.5 size-4 shrink-0" />
    );
  return (
    <p
      className={cn(
        "flex items-start gap-2 text-sm font-medium",
        state.tone === "done" && "text-success",
        state.tone === "broken" && "text-destructive",
        state.tone === "todo" && "text-warning",
        state.tone === "unknown" && "text-muted-foreground",
      )}
    >
      {icon}
      <span>{state.label}</span>
    </p>
  );
}
